import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { actions, selectors as authSelectors } from '@hixme/auth-ui'
import { STORAGE_VERIFIED } from 'modules/application-engine/constants'
import {
  ROLE_PLATFORM_HIXME_ADMIN,
  ROLE_BROKER,
  ROLE_CLIENT_ADMIN,
} from '@hixme/role-policy'
import { selectors as userSessionSelectors } from 'store/modules/user-session'
import { queryClient } from 'lib/queryClient'
import {
  useMyAbilityQuery,
  MY_ABILITY_QUERY_KEY,
} from 'enrollment-platform/rbac/useMyAbilityQuery'
import { can } from 'enrollment-platform/rbac/ability'
import { navAbilitiesForHref } from 'enrollment-platform/rbac/nav-ability-map'
import { RBAC_ENFORCEMENT_FLAG_KEY } from 'enrollment-platform/rbac/enforcement-flag'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { useStableUsRestrictedDataAccess } from 'enrollment-platform/rbac/useStableUsRestrictedDataAccess'
import {
  isDemoAllowedNavHref,
  isDemoHrAdminRestricted,
} from 'enrollment-platform/demo-restriction'
import { PROFILE } from 'routes/path-names'
import {
  clientAdminLink,
  clientLinks,
  AncillaryReportTab,
  ANCILLARY_REPORT_TAB_FLAG_KEY,
  AppManagerTab,
  AppQueueTab,
  CensusOnboardingTab,
  CompanyTab,
  DemoCompanyManagementTab,
  DocumentCenterTab,
  EmployeesTab,
  HomeTab,
  OpenEnrollmentTab,
  RecruitingTab,
  SharePointTab,
  SHAREPOINT_PORTAL_FLAG_KEY,
  textLinks,
  hixmeOnlyLinks,
  HealthPlanTab,
  PlanManagementTab,
  LEGACY_PLAN_MANAGEMENT_FLAG_KEY,
} from '../link-data'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useGlobalFlag from 'hooks/useGlobalFlag'
import { datadogRum } from '@datadog/browser-rum'
import {
  getFlagBasedAdminLinks,
  getFlagBasedClientAdminLinks,
  getFlagBasedHixmeAdminLinks,
} from '../get-flag-based-admin-links'
import { uniqBy } from 'lodash'
import useCoustomerConfiguration from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/hooks/useCustomerConfiguration'
import { useOpenEnrollmentAccess } from 'modules/annual-benefit-planning/modules/shared/presentation/controllers/use-open-enrollment-access'
import { useRecruitingAccess } from 'routes/Recruiting/hooks'
import { useCompanyAccess } from 'routes/Company/components/CompanyAccessGate'

// Hidden whenever RegionRestrictedDataBanner shows, regardless of the RBAC
// kill-switch.
const US_RESTRICTED_LINK_HREFS = new Set([
  '/enrollmentdashboard',
  '/dataandreports',
])

const dedupeLinks = (links) => uniqBy(links, 'href')

const sortLinks = (links) =>
  links.sort((a, b) => (a.sortOrder ?? 9000) - (b.sortOrder ?? 8999))

// Platform admins see these pinned in this order, then a separator, then
// every other link by name.
const PINNED_PLATFORM_ADMIN_HREFS = [HomeTab.href, EmployeesTab.href]

const pinnedRank = (link) => {
  const index = PINNED_PLATFORM_ADMIN_HREFS.indexOf(link.href)
  return index === -1 ? PINNED_PLATFORM_ADMIN_HREFS.length : index
}

const sortPlatformAdminLinks = (links) =>
  [...links].sort(
    (a, b) =>
      pinnedRank(a) - pinnedRank(b) ||
      a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })
  )

const useNavigationLinks = () => {
  const dispatch = useDispatch()
  const { flags, methods } = useFeatureFlags()
  const { isEnabled: isLegacyPlanManagementEnabled } = useGlobalFlag(
    LEGACY_PLAN_MANAGEMENT_FLAG_KEY
  )
  const { isEnabled: isAncillaryReportTabEnabled } = useGlobalFlag(
    ANCILLARY_REPORT_TAB_FLAG_KEY
  )
  const { isEnabled: isSharePointPortalEnabled } = useGlobalFlag(
    SHAREPOINT_PORTAL_FLAG_KEY
  )
  const userId = useSelector(authSelectors.getUserId)
  const role = useSelector(authSelectors.getRole, shallowEqual)
  const credentials = useSelector(authSelectors.getCredentials)
  const currentSelectedCompanyId = useSelector(
    userSessionSelectors.getCurrentCompanyId
  )
  const isCompanyProfileUndefined = useSelector(
    userSessionSelectors.isCurrentCompanyUnavailable
  )
  const currentCompany = useSelector(userSessionSelectors.getCurrentCompanyNew)
  const demoRestricted = isDemoHrAdminRestricted(role, currentCompany)

  // ! Temporary function to transition CMv1 to CMv2 --> Delete this function after the transition is complete
  const isPlatformAdmin = role === ROLE_PLATFORM_HIXME_ADMIN
  const { queryToggles } = useCoustomerConfiguration(currentSelectedCompanyId)
  const { data: configuration } = queryToggles()
  const {
    hasAccess: hasOpenEnrollmentAccess,
    isAccessLoading: isOpenEnrollmentAccessLoading,
  } = useOpenEnrollmentAccess()
  const {
    hasAccess: hasRecruitingAccess,
    isAccessLoading: isRecruitingAccessLoading,
  } = useRecruitingAccess()
  const { hasAccess: hasCompanyAccess, isLoading: isCompanyAccessLoading } =
    useCompanyAccess()
  const { hasUsRestrictedDataAccess } = useStableUsRestrictedDataAccess()
  const isCMv1Enabled = isPlatformAdmin
    ? true
    : configuration?.isCMv1Enabled || false
  const isCMv2Enabled = isPlatformAdmin
    ? true
    : configuration?.isCMv2Enabled || false

  // Loads the current user's RBAC ability. Because the sidebar mounts on every
  // authenticated page, this fires GET /me/ability right after login (not only
  // when a gated route is visited) and drives the permission-based link filter
  // below.
  const { data: abilityData } = useMyAbilityQuery()
  const abilityRules = abilityData?.rules
  // usePermission also applies the US-restricted enforcement flag.
  const { allowed: canManageUsRestrictedData } = usePermission(
    RbacActions.Manage,
    RbacSubjects.UsRestrictedData
  )

  // Re-fetch ability whenever the auth token changes. @hixme/auth-ui refreshes
  // the token (~5 min); we can't hook its refreshLogin() directly, but the
  // token value in the store changes on each refresh — invalidating here ties
  // the ability refresh to that cadence so permission changes propagate.
  const token = useSelector(authSelectors.getToken)
  useEffect(() => {
    if (!token) return
    queryClient.invalidateQueries({ queryKey: MY_ABILITY_QUERY_KEY })
  }, [token])

  // Re-fetch ability whenever the RBAC enforcement flag FLIPS (not on mount —
  // useMyAbilityQuery already refetches on mount). The sidebar is already
  // mounted when the flag transitions (e.g. turned ON mid-session, or after a
  // just-granted user's roles change), so refetchOnMount doesn't fire and the
  // menu would otherwise compute against a STALE ability (often the empty rules
  // cached while the user had no access) — collapsing to just "Sign out" even
  // though the server now grants access, and every link click then bounces to
  // Home. Invalidating on the transition forces a fresh /me/ability so the menu
  // and the route gates agree.
  const rbacFlag = flags[RBAC_ENFORCEMENT_FLAG_KEY]
  const prevRbacFlagRef = useRef(rbacFlag)
  useEffect(() => {
    if (prevRbacFlagRef.current === rbacFlag) return
    prevRbacFlagRef.current = rbacFlag
    queryClient.invalidateQueries({ queryKey: MY_ABILITY_QUERY_KEY })
  }, [rbacFlag])

  const [adminLinks, setAdminLinks] = useState([])
  const [hixmeAdminLinks, setHixmeAdminLinks] = useState([])
  const [clientAdminOnlyLinks, setClientAdminOnlyLinks] = useState([])

  useEffect(() => {
    if (!flags) return
    getFlagBasedAdminLinks(flags).then(setAdminLinks)
    getFlagBasedHixmeAdminLinks(flags).then(setHixmeAdminLinks)
  }, [flags])

  useEffect(() => {
    if (role !== ROLE_CLIENT_ADMIN) {
      setClientAdminOnlyLinks([])
      return
    }
    getFlagBasedClientAdminLinks().then(setClientAdminOnlyLinks)
  }, [role])

  useEffect(() => {
    if (userId && role) {
      const identifyData = {
        key: userId,
        firstName: credentials.firstname,
        lastName: credentials.lastname,
        custom: {
          role,
          selectedClientPK: currentSelectedCompanyId,
        },
      }

      methods.identify(identifyData)

      datadogRum.setUser({
        id: userId,
        name: `${credentials.firstname} ${credentials.lastname}`,
        role: role,
      })
    }
  }, [userId, role, credentials, currentSelectedCompanyId])

  const defaultLinks = [
    ...(demoRestricted ? [] : [{ href: PROFILE, text: 'Profile' }]),
    ...(role === ROLE_PLATFORM_HIXME_ADMIN
      ? [{ href: '/settings', text: 'Settings' }]
      : []),
    {
      href: '/login',
      text: 'Sign out',
      onClick: () => {
        localStorage.removeItem(STORAGE_VERIFIED)
        queryClient.clear()
        dispatch(actions.logout())
      },
    },
  ]

  const showRecruitingTab = !isRecruitingAccessLoading && hasRecruitingAccess

  const hixmeAdminFlagLinks = [
    ...(flags['admin.new-customer-onboarding-enabled']
      ? [CensusOnboardingTab]
      : []),
    // Demo companies are platform-admin only and staged behind a flag, so the
    // tab lives here rather than in hixmeOnlyLinks. DemoCompanyGate applies the
    // same flag to the routes, keeping the link and the pages in step.
    ...(flags['admin.customers.demo-management-enabled']
      ? [DemoCompanyManagementTab]
      : []),
    ...(isAncillaryReportTabEnabled ? [AncillaryReportTab] : []),
    ...(showRecruitingTab ? [RecruitingTab] : []),
    ...(isSharePointPortalEnabled ? [SharePointTab] : []),
  ]

  const platformHixmeAdminLinks = [...hixmeOnlyLinks, ...hixmeAdminLinks]

  // Client HR admins only — gated separately from `adminLinks` (shared with
  // Broker) since this rollout is scoped to HR admins specifically.
  const clientAdminFlagLinks = [
    ...(flags['admin.show-document-center-to-hr-admins']
      ? [DocumentCenterTab]
      : []),
    ...(showRecruitingTab ? [RecruitingTab] : []),
    ...clientAdminOnlyLinks,
  ]

  const navigationLinksRoleMapper = {
    [ROLE_PLATFORM_HIXME_ADMIN]: sortPlatformAdminLinks(
      dedupeLinks([
        ...platformHixmeAdminLinks,
        ...adminLinks,
        ...hixmeAdminFlagLinks,
      ])
    ),
    [ROLE_BROKER]: sortLinks(dedupeLinks([...clientAdminLink, ...adminLinks])),
    [ROLE_CLIENT_ADMIN]: sortLinks(
      dedupeLinks([...clientAdminLink, ...adminLinks, ...clientAdminFlagLinks])
    ),
  }

  let links = navigationLinksRoleMapper[role] || clientLinks

  // ! Temporary function to transition CMv1 to CMv2 --> Delete this function after the transition is complete
  function handleTransitionCMv1ToCMv2() {
    let _links = links
    if (!isCMv1Enabled) {
      _links = links.filter((link) => link.href !== '/census-management')
    }
    if (!isCMv2Enabled) {
      _links = links.filter(
        (link) => !['/v2-census-management'].includes(link.href)
      )
    }
    return _links
  }
  links = handleTransitionCMv1ToCMv2()

  const isCensoryEnabled = flags['admin.is-censory-enabled']
  if (!isCensoryEnabled) {
    links = links.filter((link) => link.href !== '/censory')
  }

  if (!isLegacyPlanManagementEnabled) {
    links = links.filter(
      (link) =>
        link.href !== HealthPlanTab.href && link.href !== PlanManagementTab.href
    )
  }

  const isAppEngineV1Enabled = flags['admin.app-engine.v1-enabled'] !== false
  if (!isAppEngineV1Enabled) {
    links = links.filter(
      (link) =>
        link.href !== AppManagerTab.href && link.href !== AppQueueTab.href
    )
  }

  if (isOpenEnrollmentAccessLoading || !hasOpenEnrollmentAccess) {
    links = links.filter((link) => link.href !== OpenEnrollmentTab.href)
  }

  // Mirrors CompanyAccessGate: hide Company profile from users who'd only
  // land on its Access Denied state (no Manage on UsRestrictedData).
  if (isCompanyAccessLoading || !hasCompanyAccess) {
    links = links.filter((link) => link.href !== CompanyTab.href)
  }

  if (!hasUsRestrictedDataAccess) {
    links = links.filter((link) => !US_RESTRICTED_LINK_HREFS.has(link.href))
  }

  // Demo-company HR admins get a focused, allow-listed menu: keep only the
  // demo-relevant surfaces and hide everything else. Scoped to the HR-admin
  // role via isDemoHrAdminRestricted; other roles and real companies are
  // untouched.
  if (demoRestricted) {
    links = links.filter((link) => isDemoAllowedNavHref(link.href))
  }

  // RBAC menu gating — mirrors the EPS server guard + route gates, and is gated
  // by the same kill-switch. When `admin.rbac-enforcement-enabled` is OFF (or LD
  // unavailable — the hook defaults it to false), this whole block is skipped so
  // the menu falls back to the coarse Cognito role links above, exactly as
  // before RBAC. Only when the flag is ON do we apply the DB-ability filter.
  const rbacEnforcementEnabled = !!flags[RBAC_ENFORCEMENT_FLAG_KEY]
  if (rbacEnforcementEnabled) {
    // RBAC baseline: the admin shell requires Access/AdminPortal. A user without
    // it can reach no admin surface, so once the ability has resolved we hide
    // ALL navigation and leave only "Sign out" — matching the shell gate, which
    // sends them to Home with a no-access message. (Guarded on abilityRules
    // being defined so we don't blank the menu during the initial load.)
    const lacksAdminPortal =
      !!abilityRules && !can(abilityRules, 'Access', 'AdminPortal')
    if (lacksAdminPortal) {
      return {
        links: [],
        textLinks: defaultLinks.filter((link) => link.text === 'Sign out'),
      }
    }

    // RBAC fine-grained: hide links whose surface the user's DB ability doesn't
    // grant. Links with no ability mapping pass through unchanged (additive).
    // Until the ability has loaded, RBAC-mapped links are hidden to avoid a
    // flash of a link the user may not have — they appear once /me/ability
    // resolves.
    links = links.filter((link) => {
      const required = navAbilitiesForHref(link.href)
      if (!required) return true
      return required.some((ability) =>
        ability.subject === RbacSubjects.UsRestrictedData
          ? canManageUsRestrictedData
          : can(abilityRules, ability.action, ability.subject)
      )
    })
  }

  // Runs after every filter so the separator follows whichever pinned links
  // are actually visible.
  const lastPinnedIndex =
    role === ROLE_PLATFORM_HIXME_ADMIN
      ? links.reduce(
          (last, link, index) =>
            PINNED_PLATFORM_ADMIN_HREFS.includes(link.href) ? index : last,
          -1
        )
      : -1
  const separatorIndex =
    lastPinnedIndex < links.length - 1 ? lastPinnedIndex : -1

  return {
    links: links.map((link, index) => ({
      ...link,
      disabled: link.href === '/company' && isCompanyProfileUndefined,
      separatorAfter: index === separatorIndex,
    })),
    textLinks: [...textLinks, ...defaultLinks],
  }
}

export default useNavigationLinks
